import { useEffect, useRef } from 'react';
import { Linking } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import { ModalManager } from '../Components/Web/Modal/hooks/useModalManager';
import { MODALPROMPT } from '../Components/Web/Modal/ModalPrompt/config';
import constants from '../Config/constants';
import UrlParameterHelper from '../Helper/UrlParameter';
import { useMainContext } from '../MainContext';
import routeList from '../Routes/list';
import { setDeeplinkingData } from '../RTK/noSession';
import { isGuestSelector, isLoggedInSelector } from '../RTK/user/selectors';

type DeepLinkRoutes = {
  route: string;
  reqArgs: boolean;
  parentTab?: string;
};

type GetDeepLink = (link: string) => DeepLinkRoutes;

const ratingRoute = constants.isWeb
  ? routeList.ORDER_DETAILS
  : routeList.ORDER_RATING;

// for not logged in deep linking whitelist
const DEEP_LINK_ROUTES: DeepLinkRoutes[] = [
  { route: routeList.SIGNUP_VERIFICATION_SUCCESS, reqArgs: true },
  { route: ratingRoute, reqArgs: true },
  { route: routeList.RESERVATION_TAB, reqArgs: false },
  { route: routeList.STORE, reqArgs: true }, // no need parentTab on store since it belong to the initial route of bottom tab
  {
    route: routeList.RES_STORE_DETAILS,
    reqArgs: true,
    parentTab: routeList.RESERVATION_TAB,
  },
  {
    route: routeList.RES_EVENT_DETAILS,
    reqArgs: true,
    parentTab: routeList.RESERVATION_TAB,
  },
];

const getDeepLink: GetDeepLink = (link) => {
  return lodashFind(DEEP_LINK_ROUTES, { route: link });
};

// Deep linking for when user is not logged in
const useDeepLinking = () => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const { isRemoteConfigFetching } = useMainContext();

  const isGuest = useSelector(isGuestSelector);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const deepLinkingData = useSelector(
    // @ts-ignore - must create selector for this
    (state) => state.noSession.deepLinkingData,
  );

  // effect for web to show login modal when user is not logged in and has deeplinking data
  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (constants.isWeb && !isRemoteConfigFetching) {
      const maybeShowLoginModal = async () => {
        const url = await Linking.getInitialURL();
        const urlPath = UrlParameterHelper.getUrlStringPath(url);
        if (
          !isGuest && // guest
          !isLoggedIn && // not logged in
          !lodashIsEmpty(deepLinkingData) && // no deep linking data
          urlPath !== routeList.RES_EVENT_DETAILS // not reservation event details page
        ) {
          timeoutRef.current = setTimeout(() => {
            const isReservationHome = [
              routeList.RESERVATION,
              routeList.RESERVATION_TAB,
            ].includes(urlPath as routeList);
            if (!isReservationHome) {
              ModalManager.hide(MODALPROMPT.deliveryAddress);
              ModalManager.show(MODALPROMPT.authentication, {
                payload: { isLogin: true },
              });
            }
          }, 500);
        }
      };
      maybeShowLoginModal();
    }
  }, [deepLinkingData, isGuest, isLoggedIn, isRemoteConfigFetching]);

  const _prepareDeepLinking = (deepLinkingLink: string) => {
    const deepLinkingPath =
      UrlParameterHelper.getUrlStringPath(deepLinkingLink); // get deep link path
    const deepLink = getDeepLink(deepLinkingPath);
    // clear deep linking data if:
    if (
      isGuest || // user is guest
      isLoggedIn || // user is already logged in
      !deepLinkingLink || // has no deep linking link (args)
      lodashIsEmpty(deepLink) // the link is not in the routes that has deep link
    ) {
      dispatch(setDeeplinkingData(undefined));
      return;
    }
    // if no account is logged in, save the deeplinking data so we can use it to redirect upon logging in
    const deepLinkingParams =
      UrlParameterHelper.getUrlParameter(deepLinkingLink); // get deep link params
    let shouldSetDeepLinkData = true;
    if (deepLink.reqArgs) {
      // if deepLink has requiredParams
      if (lodashIsEmpty(deepLinkingParams)) {
        shouldSetDeepLinkData = false; // if no deep link params, do not set deep link data
      } else {
        shouldSetDeepLinkData = true; // if has deep link params, set the deep link data
      }
    }
    if (shouldSetDeepLinkData) {
      dispatch(
        setDeeplinkingData({
          route: deepLinkingPath,
          params: deepLinkingParams,
        }),
      );
    } else {
      // clear deep link data
      dispatch(setDeeplinkingData(undefined));
    }
  };

  const _redirectDeepLinking = (navigation?: any, rootTab?: boolean) => {
    dispatch(setDeeplinkingData(undefined)); // clear deep linking data before redirection
    // add timeout to propagate the clearing of deep linking data, to prevent multiple times redirection
    setTimeout(
      () => {
        if (constants.isWeb) {
          window.location.href = `/${
            deepLinkingData.route
          }${UrlParameterHelper.buildQueryString(deepLinkingData.params)}`;
        } else if (navigation) {
          const deepLink = getDeepLink(deepLinkingData.route); // get the available deeplink routes using the route of deeplinkdata
          if (deepLink.parentTab && !rootTab) {
            // if the route is inside a tab, we need to load the parent tab first before navigating to the route
            navigation.navigate(deepLink.parentTab, {
              screen: deepLinkingData.route,
              params: deepLinkingData.params,
            });
          } else {
            navigation.navigate(deepLinkingData.route, deepLinkingData.params);
          }
        } else {
          throw new Error(
            'On mobile, navigation should be pass on this function',
          );
        }
      },
      constants.isWeb ? 0 : 500, // this delay is needed in order to propagate the flip of flag isLoggedIn on redux
    );
  };

  return {
    prepareDeepLinking: _prepareDeepLinking,
    redirectDeepLinking: _redirectDeepLinking,
  };
};

export default useDeepLinking;
